import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { useContentfulImages } from '@snapchat/mw-contentful-client';
import { Button as ButtonSDS, ButtonType } from '@snapchat/snap-design-system-marketing';
import type { FC } from 'react';
import { useContext } from 'react';

import { ElementLocation } from '../../context/ElementLocationContext/ElementLocationContext';
import { shouldFlushLogs } from '../../helpers/getLocalPath';
import { logger, logUserEvent } from '../../helpers/logging';
import { UserAction } from '../../types/events';
import { renderRichTextMarkingsOnly } from '../../utils/renderText/renderRichText';
import type { ButtonData } from './query';
import type { ButtonComponentProps } from './types';

export const Button: FC<ButtonData & ButtonComponentProps> = props => {
  const {
    url,
    onClick,
    buttonType,
    title,
    size,
    image,
    iconName,
    targetAsset,
    className,
    buttonTextDataset,
  } = props;
  const elementLocation = useContext(ElementLocation).elementLocation;
  const { getImageSources } = useContentfulImages();

  const onClickHandler = (): void => {
    onClick?.(); // Used by CallToAction to log a click on CTA. Kept for legacy reasons.

    logUserEvent({
      eventCategory: 'Button',
      eventAction: UserAction.Click,
      eventLabel: url ? `to: ${url}` : null,
      context: {
        elementText: documentToPlainTextString(title.json),
        targetUrl: targetAsset?.url ?? url,
        elementLocation,
      },
    });

    if (shouldFlushLogs(url)) {
      void logger.flush();
    }
  };

  const type = buttonType ?? ButtonType.Primary;
  const imgSrcs = getImageSources(image?.media?.url);
  return (
    <ButtonSDS
      className={className}
      link={targetAsset?.url ?? url}
      type={type}
      onClick={onClickHandler}
      size={size}
      iconName={iconName ?? undefined}
      imgSrcs={imgSrcs}
      imgAltText={image?.media?.title}
      buttonTextDataset={buttonTextDataset}
    >
      {renderRichTextMarkingsOnly(title)}
    </ButtonSDS>
  );
};

Button.displayName = 'Button';
